import React from 'react';
import { Render } from '@puckeditor/core';
import { useQuery } from '@apollo/client';

import { CmsResolveContentDocument } from 'src/gql/graphql';
import { isPuckData } from 'src/cms/puck-utils';
import { CMS_MODEL_ID } from 'src/cms/standalone-page/constants';
import { standalonePageRenderConfig } from 'src/cms/standalone-page-renderer/render-config';

import { useHomeMenuContext } from './home-menu-context';

// The homepage's own URL path for CMS resolution/targeting. The global homepage
// is the site root, so it resolves the standalone page published at '/'.
const HOME_URL_PATH = '/';

type HomeCmsContentProps = {
  /** Rendered when no CMS override is published/resolved for the homepage. */
  fallback: React.ReactNode;
};

/**
 * Resolves and renders the homepage's CMS content for the resolved enterprise +
 * store, feeding the geolocation-resolved `dispensaryId` into the CMS resolve
 * call for targeting (ECOMM-11826).
 *
 * Falls back to the generic marketplace homepage while loading and whenever no
 * CMS page has been published/resolved for this enterprise/store, so enabling the
 * resolution never blanks the homepage.
 */
export function HomeCmsContent({ fallback }: HomeCmsContentProps): JSX.Element {
  const menuContext = useHomeMenuContext();
  const enterpriseId = menuContext?.enterpriseId;
  const dispensaryId = menuContext?.dispensaryId;

  const { data, loading } = useQuery(CmsResolveContentDocument, {
    variables: {
      enterpriseId: enterpriseId ?? '',
      modelId: CMS_MODEL_ID,
      dispensaryId,
      urlPath: HOME_URL_PATH,
    },
    skip: !enterpriseId,
  });

  if (loading) {
    return <>{fallback}</>;
  }

  const pageData = data?.cmsResolveContent?.data;
  if (!isPuckData(pageData)) {
    return <>{fallback}</>;
  }

  return <Render config={standalonePageRenderConfig} data={pageData} />;
}
